import { useState } from 'react'
import { useForm } from '@tanstack/react-form'
import { Schema } from 'effect'
import { useTranslation } from 'react-i18next'
import { Building2, Check, IdCard } from 'lucide-react'
import { useCheckInSend, useCheckInSelector } from '../context'
import { checkInSelectors } from '../selectors'
import { PatientDetailsSchema, type PatientDetailsData } from '../schema'
import { useAudio } from '../../../../../lib/use-audio'
import { kioskOperationsClient } from '../../../../../lib/rpc/kiosk-operations.client'
import { KioskStep } from '../../../../../components/kiosk-step'
import { KioskTextField } from '../../../../../components/kiosk-field'
import { KioskStatePanel } from '../../../../../components/kiosk-state-panel'
import { formatDateOfBirthForDisplay } from '../../../components/DateOfBirthInput'
import { insuranceTypeLabelKey } from './registration-display'
import { RegistrationSummary, type RegistrationSummaryRow } from './registration-summary'

/**
 * Map internal gender codes to DocCirrus gender values
 */
function mapGender(gender?: 'M' | 'W' | 'D' | 'U'): 'MALE' | 'FEMALE' | 'VARIOUS' | 'UNKNOWN' {
  switch (gender) {
    case 'M':
      return 'MALE'
    case 'W':
      return 'FEMALE'
    case 'D':
      return 'VARIOUS'
    case 'U':
    default:
      return 'UNKNOWN'
  }
}

/**
 * Determine talk/salutation based on gender
 */
function determineTalk(gender?: 'M' | 'W' | 'D' | 'U'): 'MR' | 'MS' | 'NONE' {
  switch (gender) {
    case 'M':
      return 'MR'
    case 'W':
      return 'MS'
    default:
      return 'NONE'
  }
}

/**
 * Patient registration details step: the patient checks a readable summary
 * (German date, insurance type in words, insurer name, gender) and finishes
 * with the footer action (KC-76, KC-77).
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <PatientRegisterDetailsStep />
 * ```
 */
export function PatientRegisterDetailsStep() {
  const { t } = useTranslation()
  const send = useCheckInSend()
  const registration = useCheckInSelector(checkInSelectors.registration)
  const insurance = useCheckInSelector(checkInSelectors.insurance)
  const [isCreating, setIsCreating] = useState(false)
  const [error, setError] = useState<string>()

  // Play audio on mount
  useAudio('checkIn-patientRegisterDetails')

  const form = useForm({
    defaultValues: {
      policyNumber: ''
    } as PatientDetailsData,
    validators: {
      onChange: Schema.toStandardSchemaV1(PatientDetailsSchema)
    },
    onSubmit: async ({ value }) => {
      if (!registration?.givenName || !registration?.familyName || !registration?.dob) {
        setError(t('self-service.check-in.register-details.missing-data'))
        return
      }

      setIsCreating(true)
      setError(undefined)

      try {
        const outcome = await kioskOperationsClient.createPatient({
          firstname: registration.givenName,
          lastname: registration.familyName,
          dob: registration.dob,
          gender: mapGender(registration.gender),
          talk: determineTalk(registration.gender),
          insurance: insurance ?? 'SELFPAY',
          insurerId: registration.insurerId ?? undefined,
          insurerName: registration.insurerName,
          policyNumber: value.policyNumber || undefined
        })

        if (outcome.kind === 'policy-restricted') {
          send({ type: 'PATIENT_CREATION_RESTRICTED' })
          return
        }

        if (outcome.kind === 'failed') {
          setError(t('self-service.check-in.register-from-card.create-error-v2'))
          return
        }

        send({ type: 'PATIENT_CREATED', patientId: outcome.patientId })
      } catch {
        setError(t('self-service.check-in.register-from-card.create-error-v2'))
      } finally {
        setIsCreating(false)
      }
    }
  })

  const insurerLabel = registration?.insurerDisplayName ?? registration?.insurerName
  const rows: RegistrationSummaryRow[] = [
    {
      label: t('self-service.check-in.register-from-card.name'),
      value: [registration?.givenName, registration?.familyName].filter(Boolean).join(' '),
    },
    {
      label: t('self-service.check-in.register-basics.dob'),
      value: registration?.dob ? formatDateOfBirthForDisplay(registration.dob) : '—',
    },
    ...(registration?.gender
      ? [
          {
            label: t('self-service.check-in.register-basics.gender'),
            value: t(`common.gender.${registration.gender}`),
          },
        ]
      : []),
    ...(insurance
      ? [
          {
            label: t('self-service.check-in.register-basics.insurance-type'),
            value: t(insuranceTypeLabelKey(insurance)),
          },
        ]
      : []),
    ...(insurerLabel
      ? [
          {
            label: t('self-service.check-in.register-from-card.insurer'),
            value: insurerLabel,
          },
        ]
      : []),
  ]

  return (
    <KioskStep
      width="narrow"
      title={t('self-service.check-in.register-details.title')}
      lead={t('self-service.check-in.register-details.subtitle')}
      primaryAction={{
        label: t('self-service.check-in.register-details.create-v2'),
        icon: Check,
        onClick: () => void form.handleSubmit(),
        busy: isCreating,
        testId: 'frontdesk-register-details-create-btn',
      }}
      secondaryAction={
        error === undefined
          ? undefined
          : {
              label: t('self-service.help-reception'),
              icon: Building2,
              onClick: () => send({ type: 'RECEPTION_HELP' }),
              testId: 'frontdesk-register-details-reception-btn',
            }
      }
    >
      <form
        data-testid="frontdesk-register-details-form"
        className="flex flex-col gap-6"
        onSubmit={(event) => {
          event.preventDefault()
          void form.handleSubmit()
        }}
      >
        <RegistrationSummary rows={rows} testId="frontdesk-register-details-summary" />

        {insurance === 'PKV' ? (
          <form.Field name="policyNumber">
            {(field) => (
              <KioskTextField
                id="checkin-register-policy-number"
                testId="frontdesk-register-details-policy-number-input"
                label={t('self-service.check-in.register-details.policy-number')}
                description={t('self-service.check-in.register-details.policy-number-hint')}
                icon={IdCard}
                value={field.state.value ?? ''}
                onChange={field.handleChange}
                onBlur={field.handleBlur}
              />
            )}
          </form.Field>
        ) : null}

        {error ? (
          <KioskStatePanel
            tone="error"
            title={error}
            testId="frontdesk-register-details-error"
          />
        ) : null}
      </form>
    </KioskStep>
  )
}
